import { FeatureGate } from "@/components/common/FeatureGate"
import { RequirePermission } from "@/components/common/RequirePermission"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { CompanyIndexPage } from "@/features/company-index/CompanyIndexPage"
import { CompanyProfilePage } from "@/features/company-index/CompanyProfilePage"
import { CatalogGate } from "@/hooks/useTranslation/CatalogGate"
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router"
import { CustomerRequestsModule } from "./components/customer-requests/CustomerRequestsModule"
import { ConformityEditorModule } from "./components/customer-requests/conformity/ConformityEditorModule"
import { ConformityInboxModule } from "./components/customer-requests/conformity/ConformityInboxModule"
import { COVER_CATALOGS } from "./components/customer-requests/conformity/conformityCoverDefaults"
import { ConformityPage } from "./components/customer-requests/pages/ConformityPage"
import { CustomerDashboardPage } from "./components/customer-requests/pages/CustomerDashboardPage"
import { CustomerRequestConfirmPage } from "./components/customer-requests/pages/CustomerRequestConfirmPage"
import { CustomerRequestImportPage } from "./components/customer-requests/pages/CustomerRequestImportPage"
import { CustomerRequestRecordPage } from "./components/customer-requests/pages/CustomerRequestRecordPage"
import { GapsStep } from "./components/customer-requests/processing/GapsStep"
import { MappingStep } from "./components/customer-requests/processing/MappingStep"
import { QuestionnaireProcessingLayout } from "./components/customer-requests/processing/QuestionnaireProcessingLayout"
import { ReconcileStep } from "./components/customer-requests/processing/ReconcileStep"
import { ReviewStep } from "./components/customer-requests/processing/ReviewStep"
import { DeclarationImportPage } from "./components/declarations/pages/DeclarationImportPage"
import { HomePage } from "./components/home/HomePage"
import { MasterDataImportModule } from "./components/master-data/MasterDataImportModule"
import { MasterDataLayout } from "./components/master-data/MasterDataLayout"
import { MasterDataMappingModule } from "./components/master-data/MasterDataMappingModule"
import { MasterDataProcessingModule } from "./components/master-data/MasterDataProcessingModule"
import { ArticlesPage } from "./components/master-data/pages/ArticlesPage"
import { CustomersPage } from "./components/master-data/pages/CustomersPage"
import { ProductsPage } from "./components/master-data/pages/ProductsPage"
import { SubstancesPage } from "./components/master-data/pages/SubstancesPage"
import { SuppliersPage } from "./components/master-data/pages/SuppliersPage"
import { SupplierStudioLayout } from "./components/supplier-declarations/SupplierStudioLayout"
import { INQUIRY_DOCUMENT_CATALOGS } from "./components/supplier-declarations/inquiry/draft/inquiryDraftMock"
import { DashboardPage } from "./components/supplier-declarations/pages/DashboardPage"
import { DeclarationReviewPage } from "./components/supplier-declarations/pages/DeclarationReviewPage"
import { DeclarationsPage } from "./components/supplier-declarations/pages/DeclarationsPage"
import { InquiryDraftPage } from "./components/supplier-declarations/pages/InquiryDraftPage"
import { InquiryDraftSuccessPage } from "./components/supplier-declarations/pages/InquiryDraftSuccessPage"
import { InquiryPage } from "./components/supplier-declarations/pages/InquiryPage"
import { NewInquiryPage } from "./components/supplier-declarations/pages/NewInquiryPage"

// Legacy redirect: the supplier-studio segment was briefly `supplier-declarations`
// (PR #1394) before being restored. Preserve old deep links/bookmarks by redirecting
// the legacy path — and any sub-path — to the current one.
function LegacyStudioRedirect() {
  const { pathname, search } = useLocation()
  const to =
    pathname.replace("/supplier-declarations", "/supplier-studio") + search
  return <Navigate to={to} replace />
}

function LegacyCompanyIndexRedirect() {
  const { pathname, search } = useLocation()
  return (
    <Navigate
      to={
        pathname.replace("/supplier-studio/company-index", "/company-index") +
        search
      }
      replace
    />
  )
}

/**
 * Module index: the start page when MC_HOME is enabled, otherwise the
 * pre-existing redirect into Master Data. Exported for tests.
 */
export function McIndexRoute() {
  const homeEnabled = useFeatureFlag("MC_HOME")
  return homeEnabled ? <HomePage /> : <Navigate to="master-data" replace />
}

export default function MaterialComplianceApp() {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* `min-h-0` is load-bearing: without it this column flex item keeps its
          default `min-height: auto` and cannot shrink below its content, so a
          full-height route (the v2 conformity editor and its resizable panes)
          has no definite parent height for `h-full` to resolve against. The
          div then grows past the viewport and the wrapper's `overflow-hidden`
          clips the bottom instead of this one scrolling — the editor's footer
          and the refused-approval panel were being cut off that way. */}
      <div className="min-h-0 flex-1 overflow-auto bg-grey2">
        <Routes>
          <Route index element={<McIndexRoute />} />
          <Route
            path="company-index"
            element={
              <FeatureGate flag="COMPANY_INDEX" parentFlag="MC_SUPPLIER_STUDIO">
                <RequirePermission
                  permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                >
                  <CompanyIndexPage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="company-index/:companyId"
            element={
              <FeatureGate flag="COMPANY_INDEX" parentFlag="MC_SUPPLIER_STUDIO">
                <RequirePermission
                  permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                >
                  <CompanyProfilePage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="master-data/import"
            element={
              <FeatureGate flag="MC_MASTER_DATA">
                <RequirePermission permission={PERMISSIONS.MC_MASTER_DATA_VIEW}>
                  <MasterDataImportModule />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="master-data/processing"
            element={
              <FeatureGate flag="MC_MASTER_DATA">
                <RequirePermission permission={PERMISSIONS.MC_MASTER_DATA_VIEW}>
                  <MasterDataProcessingModule />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="master-data/mapping"
            element={
              <FeatureGate flag="MC_MASTER_DATA">
                <RequirePermission permission={PERMISSIONS.MC_MASTER_DATA_VIEW}>
                  <MasterDataMappingModule />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="master-data"
            element={
              <FeatureGate flag="MC_MASTER_DATA">
                <RequirePermission permission={PERMISSIONS.MC_MASTER_DATA_VIEW}>
                  <MasterDataLayout />
                </RequirePermission>
              </FeatureGate>
            }
          >
            <Route index element={<Navigate to="suppliers" replace />} />
            <Route
              path="suppliers"
              element={
                <FeatureGate flag="MC_MASTER_DATA_SUPPLIERS">
                  <SuppliersPage />
                </FeatureGate>
              }
            />
            <Route
              path="articles"
              element={
                <FeatureGate flag="MC_MASTER_DATA_ARTICLES">
                  <ArticlesPage />
                </FeatureGate>
              }
            />
            <Route
              path="substances"
              element={
                <FeatureGate flag="MC_MASTER_DATA_SUBSTANCES">
                  <SubstancesPage />
                </FeatureGate>
              }
            />
            <Route
              path="products"
              element={
                <FeatureGate flag="MC_MASTER_DATA_PRODUCTS">
                  <ProductsPage />
                </FeatureGate>
              }
            />
            <Route
              path="customers"
              element={
                <FeatureGate flag="MC_MASTER_DATA_CUSTOMERS">
                  <CustomersPage />
                </FeatureGate>
              }
            />
          </Route>
          <Route
            path="customer-studio/dashboard"
            element={
              <FeatureGate
                flag="MC_CUSTOMER_STUDIO_DASHBOARD"
                parentFlag="MC_CUSTOMER_STUDIO"
              >
                <RequirePermission
                  permission={PERMISSIONS.MC_QUESTIONNAIRES_VIEW}
                >
                  <CustomerDashboardPage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          {/* Conformity — landing = inbox (list of every declaration for the
              org), /new = the Generate wizard, /edit/:id = editor. The old
              /review route folded into the inbox with ?ids=<batch> so post-
              Generate lands on the inbox filtered to the just-generated batch.
              Order matters: literal segments (`new`, `edit/:id`) precede the
              bare `conformity` so React Router matches them first. Each is
              gated by MC_CUSTOMER_STUDIO_CONFORMITY (parent MC_CUSTOMER_STUDIO)
              so the whole submodule can be flipped off atomically. */}
          <Route
            path="customer-studio/conformity/edit/:declarationId"
            element={
              <FeatureGate
                flag="MC_CUSTOMER_STUDIO_CONFORMITY"
                parentFlag="MC_CUSTOMER_STUDIO"
              >
                <RequirePermission
                  permission={PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW}
                >
                  <CatalogGate languages={COVER_CATALOGS}>
                    <ConformityEditorModule />
                  </CatalogGate>
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="customer-studio/conformity/new"
            element={
              <FeatureGate
                flag="MC_CUSTOMER_STUDIO_CONFORMITY"
                parentFlag="MC_CUSTOMER_STUDIO"
              >
                <RequirePermission
                  permission={PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW}
                >
                  <ConformityPage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="customer-studio/conformity"
            element={
              <FeatureGate
                flag="MC_CUSTOMER_STUDIO_CONFORMITY"
                parentFlag="MC_CUSTOMER_STUDIO"
              >
                <RequirePermission
                  permission={PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW}
                >
                  <ConformityInboxModule />
                </RequirePermission>
              </FeatureGate>
            }
          />

          {/* Requests — nested under `customer-studio/requests` per main's
              restructure (was flat under `customer-studio/*` on this branch
              before the merge). Whole subtree gated by a feature flag on the
              parent Route via <Outlet /> so children inherit the check
              without repeating <FeatureGate> on every leaf. */}
          <Route
            path="customer-studio/requests"
            element={
              <FeatureGate
                flag="MC_CUSTOMER_STUDIO_REQUESTS"
                parentFlag="MC_CUSTOMER_STUDIO"
              >
                <RequirePermission
                  permission={PERMISSIONS.MC_QUESTIONNAIRES_VIEW}
                >
                  <Outlet />
                </RequirePermission>
              </FeatureGate>
            }
          >
            <Route
              index
              element={
                <div className="p-6">
                  <CustomerRequestsModule />
                </div>
              }
            />
            <Route
              path="import/confirm"
              element={
                <div className="h-full">
                  <CustomerRequestConfirmPage />
                </div>
              }
            />
            <Route
              path="import"
              element={
                <div className="h-full p-6">
                  <CustomerRequestImportPage />
                </div>
              }
            />
            <Route
              path=":id"
              element={
                <div className="p-6">
                  <CustomerRequestRecordPage />
                </div>
              }
            />
            <Route
              path=":id/process"
              element={<QuestionnaireProcessingLayout />}
            >
              {/* No index element on purpose: landing on the bare .../process
                  path with no step segment must fall through to
                  QuestionnaireProcessingLayout's own redirect effect, which
                  resolves the canonical step from processing_status. A
                  hardcoded `<Navigate to="mapping">` here would always win
                  (mapping is always a "reachable" revisit target), permanently
                  stranding any resumed request on the mapping step. */}
              <Route path="mapping" element={<MappingStep />} />
              <Route path="reconcile" element={<ReconcileStep />} />
              <Route path="gaps" element={<GapsStep />} />
              <Route path="review" element={<ReviewStep />} />
            </Route>
          </Route>
          <Route
            path="supplier-studio/inquiry/draft/success"
            element={
              <FeatureGate
                flag="MC_SUPPLIER_STUDIO_INQUIRY"
                parentFlag="MC_SUPPLIER_STUDIO"
              >
                <RequirePermission permission={PERMISSIONS.MC_INQUIRIES_VIEW}>
                  <InquiryDraftSuccessPage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="supplier-studio/inquiry/draft"
            element={
              <FeatureGate
                flag="MC_SUPPLIER_STUDIO_INQUIRY"
                parentFlag="MC_SUPPLIER_STUDIO"
              >
                <RequirePermission permission={PERMISSIONS.MC_INQUIRIES_VIEW}>
                  <CatalogGate languages={INQUIRY_DOCUMENT_CATALOGS}>
                    <InquiryDraftPage />
                  </CatalogGate>
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="supplier-studio/inquiry/new"
            element={
              <FeatureGate
                flag="MC_SUPPLIER_STUDIO_INQUIRY"
                parentFlag="MC_SUPPLIER_STUDIO"
              >
                <RequirePermission permission={PERMISSIONS.MC_INQUIRIES_VIEW}>
                  <NewInquiryPage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          {/* Full-screen review wizard — kept outside SupplierStudioLayout so its
              header/footer run full-bleed, matching the inquiry draft flow. */}
          <Route
            path="supplier-studio/declarations/:id/review"
            element={
              <FeatureGate
                flag="MC_SUPPLIER_STUDIO_DECLARATIONS"
                parentFlag="MC_SUPPLIER_STUDIO"
              >
                <RequirePermission
                  permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                >
                  <DeclarationReviewPage />
                </RequirePermission>
              </FeatureGate>
            }
          />
          <Route
            path="supplier-studio"
            element={
              <FeatureGate flag="MC_SUPPLIER_STUDIO">
                <SupplierStudioLayout />
              </FeatureGate>
            }
          >
            <Route index element={<Navigate to="declarations" replace />} />
            <Route
              path="company-index"
              element={
                <FeatureGate flag="COMPANY_INDEX">
                  <RequirePermission
                    permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                  >
                    <LegacyCompanyIndexRedirect />
                  </RequirePermission>
                </FeatureGate>
              }
            />
            <Route
              path="company-index/:companyId"
              element={
                <FeatureGate flag="COMPANY_INDEX">
                  <RequirePermission
                    permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                  >
                    <LegacyCompanyIndexRedirect />
                  </RequirePermission>
                </FeatureGate>
              }
            />
            <Route
              path="dashboard"
              element={
                <FeatureGate flag="MC_SUPPLIER_STUDIO_DASHBOARD">
                  <RequirePermission
                    permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                  >
                    <DashboardPage />
                  </RequirePermission>
                </FeatureGate>
              }
            />
            <Route
              path="declarations"
              element={
                <FeatureGate flag="MC_SUPPLIER_STUDIO_DECLARATIONS">
                  <RequirePermission
                    permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                  >
                    <DeclarationsPage />
                  </RequirePermission>
                </FeatureGate>
              }
            />
            <Route
              path="declarations/import"
              element={
                <FeatureGate flag="MC_SUPPLIER_STUDIO_DECLARATIONS">
                  <RequirePermission
                    permission={PERMISSIONS.MC_DECLARATIONS_VIEW}
                  >
                    <div className="h-full p-6">
                      <DeclarationImportPage />
                    </div>
                  </RequirePermission>
                </FeatureGate>
              }
            />
            <Route
              path="inquiry"
              element={
                <FeatureGate flag="MC_SUPPLIER_STUDIO_INQUIRY">
                  <RequirePermission permission={PERMISSIONS.MC_INQUIRIES_VIEW}>
                    <InquiryPage />
                  </RequirePermission>
                </FeatureGate>
              }
            />
          </Route>
          <Route
            path="supplier-declarations/*"
            element={<LegacyStudioRedirect />}
          />
          {/* Absolute on purpose: since React Router v7 a relative path inside a
              splat route resolves against the full matched URL (splat included),
              so `to="master-data"` from /material-compliance/foo would go to
              /material-compliance/foo/master-data and match this route again. */}
          <Route
            path="*"
            element={<Navigate to="/material-compliance/master-data" replace />}
          />
        </Routes>
      </div>
    </div>
  )
}
